import React, { useState } from "react";
import { useLang } from "@/i18n";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { api } from "@/api";
import DrawingCanvas from "./DrawingCanvas";
import { OrnateHeartDivider } from "./FloralOrnaments";

export default function MessageSection({ slug, requireName = false }) {
    const { t, dir } = useLang();
    const [text, setText] = useState("");
    const [senderName, setSenderName] = useState("");
    const [loading, setLoading] = useState(false);

    const sendText = async () => {
        if (!text.trim()) return;
        if (requireName && !senderName.trim()) {
            toast.error(t("enterYourName"));
            return;
        }
        setLoading(true);
        try {
            await api.post(`/invite/${slug}/message`, {
                type: "text",
                text,
                sender_name: senderName,
            });
            toast.success(t("messageSent"));
            setText("");
            setSenderName("");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Error");
        } finally {
            setLoading(false);
        }
    };

    const sendDrawing = async (payload) => {
        setLoading(true);
        try {
            await api.post(`/invite/${slug}/message`, payload);
            toast.success(t("messageSent"));
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Error");
        } finally {
            setLoading(false);
        }
    };

    return (
        <section data-testid="message-section" className="py-16 px-6 wd-paper">
            <div className="max-w-xl mx-auto">
                <div className="text-center">
                    <h3 className="wd-heading text-3xl md:text-4xl text-[var(--wd-primary)] mt-2">
                        {t("leaveHandwritten")}
                    </h3>
                    <div className="mt-6 flex justify-center"><OrnateHeartDivider /></div>
                </div>

                <div className="wd-card mt-10 p-6 md:p-8" dir={dir}>
                    <Tabs defaultValue="written" className="w-full">
                        <TabsList className="grid grid-cols-2 bg-[var(--wd-accent-soft)]/40 rounded-full p-1 mx-auto max-w-sm">
                            <TabsTrigger
                                value="drawn"
                                data-testid="tab-drawn"
                                className="rounded-full data-[state=active]:bg-[var(--wd-primary)] data-[state=active]:text-white"
                            >
                                {t("drawnMessage")}
                            </TabsTrigger>
                            <TabsTrigger
                                value="written"
                                data-testid="tab-written"
                                className="rounded-full data-[state=active]:bg-[var(--wd-primary)] data-[state=active]:text-white"
                            >
                                {t("writtenMessage")}
                            </TabsTrigger>
                        </TabsList>

                        <TabsContent value="drawn" className="mt-6">
                            <DrawingCanvas onSend={sendDrawing} disabled={loading} requireName={requireName} />
                        </TabsContent>
                        <TabsContent value="written" className="mt-6">
                            <div className="space-y-4">
                                <textarea
                                    value={text}
                                    onChange={(e) => setText(e.target.value)}
                                    placeholder={t("writeMessageHere")}
                                    rows={5}
                                    className="wd-input resize-none border-b-2 border-[var(--wd-accent)] rounded-xl bg-white/60 p-4"
                                    data-testid="written-message-textarea"
                                />
                                <input
                                    type="text"
                                    value={senderName}
                                    onChange={(e) => setSenderName(e.target.value)}
                                    placeholder={t("yourName")}
                                    className="wd-input"
                                    data-testid="written-sender-name"
                                />
                                <div className="flex items-center gap-3">
                                    <button
                                        type="button"
                                        onClick={() => setText("")}
                                        className="wd-btn wd-btn-ghost"
                                        data-testid="written-clear-btn"
                                    >
                                        {t("clear")}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={sendText}
                                        disabled={loading || !text.trim()}
                                        className="wd-btn flex-1"
                                        data-testid="written-send-btn"
                                    >
                                        {t("sendMessage")}
                                    </button>
                                </div>
                            </div>
                        </TabsContent>
                    </Tabs>
                </div>
            </div>
        </section>
    );
}
